/*
 * INTEX CUSTOM — not part of vanilla Ignite. See INTEX-CHANGES.md §14.39.
 *
 * Fills the model panel shell that sections/intex-main-collection.liquid
 * renders when Liquid could not resolve the shopper's model.
 *
 * Why this exists. The panel's mode is decided from collection.filters, which
 * is a view of the NARROWED result set, not a record of what the shopper
 * picked — "Only filters relevant to the current collection are returned".
 * On a page filtered to one model the compat_models filter can come back with
 * no active value (§14.36), or be dropped from collection.filters altogether
 * once there is nothing left to choose (the Challenger 2 case). Liquid has no
 * other route: the request object carries no query string. So when the server
 * could not name the model, this fills in the shell from the GID in the URL.
 *
 * The server-rendered panel stays the fast path. This script does nothing at
 * all on a page where Liquid did resolve the model — there is no shell to fill.
 *
 * No layout jump: the column and the shell are already in the HTML, the inline
 * script in the section settles the layout before first paint, and the skeleton
 * reserves the height of each part it is standing in for. Arriving from a
 * header search the model data is already in sessionStorage, so the fill is
 * synchronous on first run and nothing visibly changes at all.
 *
 * It also owns the two behaviours every model panel has, shell or
 * server-rendered: Change model opening the header finder (§14.37, §14.45)
 * and manual links opening in-tab (§14.41).
 */
(() => {
  const MODEL_PARAM = 'filter.p.m.custom.compat_models';

  /* The key assets/intex-compat-picker.js already uses. Reused rather than
     given one of its own, because the data is identical and arriving from a
     header search means it is already warm — the common case then needs no
     request at all. Written in the same {models} shape. (It was shared with
     assets/intex-model-banner.js too, until §14.41 deleted that file.) */
  const CACHE_KEY = 'intexCompatModels:v4';

  const normalise = value => (value == null ? '' : String(value)).trim();

  function shell() {
    return document.querySelector('[data-intex-panel-shell]');
  }

  function readCache() {
    try {
      const raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.models) && parsed.models.length ? parsed.models : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(models) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ models }));
    } catch (error) {
      /* Private mode or a full quota. The panel still fills from the fetch. */
    }
  }

  /* Mirrors intex-compat-picker.js modelsRequestUrl(): resolved against the
     origin, `view` always re-stamped, `preview_theme_id` forwarded, and NO
     Accept header — Shopify answers `Accept: application/json` on a collection
     URL with its own JSON instead of rendering the `?view=` template. */
  function modelsRequestUrl() {
    const picker = document.querySelector('intex-compat-picker[data-models-url]');
    const base = picker?.dataset.modelsUrl || '/collections/all-parts';

    const url = new URL(base, window.location.origin);
    url.searchParams.set('view', 'intex-models');

    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);

    return url.toString();
  }

  async function loadModels() {
    const url = modelsRequestUrl();
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText} fetching ${url}`);

    const body = await response.text();

    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch (error) {
      const type = response.headers.get('content-type') || 'no content-type';
      throw new Error(`${url} did not return JSON (${type}). First 160 chars: ${body.slice(0, 160).replace(/\s+/g, ' ').trim()}`);
    }

    if (!Array.isArray(parsed.models)) {
      throw new Error(`${url} has no "models" array (keys: ${Object.keys(parsed).join(', ') || '(no keys)'}).`);
    }

    if (parsed.models.length) writeCache(parsed.models);
    return parsed.models;
  }

  /* ------------------------------------------------------------------ FILL */

  function setText(root, selector, value) {
    const element = root.querySelector(selector);
    if (!element) return false;

    const text = normalise(value);
    element.textContent = text;
    element.hidden = !text;
    return Boolean(text);
  }

  function fillSpec(root, model) {
    /* Display order. The keys are the data- hooks on the rows and cells:
       data-intex-panel-spec-row="<key>" and data-intex-panel-<key>. */
    const rows = [
      ['number', model.number],
      ['years', model.year],
      ['category', model.category],
      ['type', model.type]
    ];

    let any = false;

    rows.forEach(([key, value]) => {
      const row = root.querySelector(`[data-intex-panel-spec-row="${key}"]`);
      const text = normalise(value);
      if (row) row.hidden = !text;
      if (text) any = true;

      const cell = root.querySelector(`[data-intex-panel-${key}]`);
      if (cell) cell.textContent = text;
    });

    const spec = root.querySelector('[data-intex-panel-spec]');
    if (spec) spec.hidden = !any;
  }

  /* Sets Shopify's CDN width parameter on an image URL, keeping the rest. A
     URL that will not parse is returned as it came. */
  function sizedImage(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return src.startsWith('//') ? url.toString().replace(/^https?:/, '') : url.toString();
    } catch (error) {
      return src;
    }
  }

  function fillMedia(root, model) {
    const media = root.querySelector('[data-intex-panel-media]');
    if (!media) return;

    const src = normalise(model.image);
    const image = media.querySelector('img');

    if (!src || !image) {
      media.hidden = true;
      return;
    }

    /* The endpoint's image is sized for the picker thumbnails (width=200).
       The card is filled edge to edge now (§14.64), so ask the CDN for the
       same 440 / 880 widths the server-rendered card uses. */
    image.src = sizedImage(src, 880);
    image.srcset = `${sizedImage(src, 440)} 1x, ${sizedImage(src, 880)} 2x`;
    image.alt = normalise(model.name);
    media.hidden = false;
  }

  /* There are no stored manual titles, so labels follow the same index
     convention sections/manuals-hub.liquid uses, which is what
     the deleted banner used as well: one file is just the manual; with
     several, index 0 is the manual and index 1 the troubleshooting guides. */
  function manualLabel(index, total) {
    if (total === 1) return 'View manual';
    if (index === 0) return 'Manual';
    if (index === 1) return 'Troubleshooting Guides';
    return `Manual ${index + 1}`;
  }

  /* The same two shapes the server-rendered panel produces: one manual is an
     <a>, several are a <details> with one row each. Both carry .intex-btn,
     which is what bindManuals() below matches on, plus .intex-btn--link for
     the teal underlined text-link look (§27). */
  function fillManuals(root, model) {
    const slot = root.querySelector('[data-intex-panel-manuals]');
    if (!slot) return;

    const manuals = Array.isArray(model.manuals) ? model.manuals.filter(Boolean) : [];
    slot.textContent = '';
    if (!manuals.length) return;

    if (manuals.length === 1) {
      const link = document.createElement('a');
      link.className = 'intex-btn intex-btn--link';
      link.href = manuals[0];
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = slot.dataset.manualLabelOne || 'View manual';
      slot.appendChild(link);
      return;
    }

    const details = document.createElement('details');
    details.className = 'intex-manuals';

    const summary = document.createElement('summary');
    summary.className = 'intex-btn intex-btn--link intex-manuals__summary';
    summary.textContent = (slot.dataset.manualLabelMany || 'Manuals ([count])')
      .replace('[count]', String(manuals.length));
    details.appendChild(summary);

    const list = document.createElement('ul');
    list.className = 'intex-manuals__list';
    list.setAttribute('role', 'list');

    manuals.forEach((url, index) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'intex-manuals__link';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = manualLabel(index, manuals.length);
      item.appendChild(link);
      list.appendChild(item);
    });

    details.appendChild(list);
    slot.appendChild(details);
  }

  function fill(root, model) {
    setText(root, '[data-intex-panel-title]', model.name);
    fillMedia(root, model);
    fillSpec(root, model);
    fillManuals(root, model);
    root.classList.remove('intex-panel--skeleton');
  }

  /* Everything the shell can show before the model data arrives: the name the
     finder already put in the URL, and the GID the Change model button needs to
     find the shopper's saved entry (§14.37). */
  function fillFromUrl(root, gid, title) {
    root.setAttribute('data-intex-model-gid', gid);
    if (title) setText(root, '[data-intex-panel-title]', title);
  }

  /* ------------------------------------------------ CHANGE MODEL (§14.37)

     Opens the header finder in place — the same finder the header chip uses,
     never a second picker. openForm(entry) when the shopper already has this
     model saved, so they land in the form on their own entry and editing it
     updates that entry instead of adding a near-duplicate; open() otherwise.
     Both are the finder's own methods (assets/compatibility-finder.js); no
     storage is read or written here.

     Bound on each panel, NOT delegated from document, and that is the point
     (§14.45). Benchmark's onDocumentClick is itself a document listener that
     closes the finder on any click outside it, and stopPropagation() inside
     one document listener does not stop another on the same node — so the old
     inline handler in the snippet opened the finder and Benchmark closed it
     again inside the same click. Stopping the event at the panel means it never
     reaches document at all. Same reason assets/intex-finder-cta.js binds on
     its own elements rather than on document.

     The <a> keeps its real href, so with JS off, with no finder on the page, or
     on a modifier-click, the click just navigates — nothing here runs
     preventDefault unless it can actually open something. */
  function onChangeModel(event) {
    const trigger = event.target.closest('[data-intex-open-finder]');
    if (!trigger) return;

    /* Let the browser handle cmd/ctrl-click, shift-click and alt-click. */
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const finder = document.querySelector('compatibility-finder');
    if (!finder || typeof finder.open !== 'function') return;

    event.preventDefault();
    event.stopPropagation();

    const panel = trigger.closest('[data-intex-model-gid]');
    const gid = panel ? normalise(panel.getAttribute('data-intex-model-gid')) : '';

    let entry = null;
    if (gid && typeof finder.read === 'function') {
      entry = finder.read().find(candidate =>
        (candidate.filters || []).some(filter => filter && filter.value === gid)
      ) || null;
    }

    /* open() first in both cases: openForm() shows the form but does not open
       the dropdown itself, so on its own it would fill a panel nobody can see.
       Same order as openEdit() in assets/intex-finder-cta.js. */
    finder.open();

    if (entry && typeof finder.openForm === 'function') {
      finder.openForm(entry);
    }
  }

  /* Once per panel, and safe to call again — the theme editor re-renders the
     section (shopify:section:load) and the new panels need binding too. */
  function bindChangeModel() {
    document.querySelectorAll('[data-intex-model-panel]').forEach(panel => {
      if (panel.__intexChangeBound) return;
      panel.__intexChangeBound = true;
      panel.addEventListener('click', onChangeModel);
    });
  }

  /* ------------------------------------------- MANUALS OPEN IN-TAB (§14.41)

     Ported from the deleted assets/intex-model-banner.js before it went,
     and widened: it is a delegated listener on every model panel, so
     it covers the SERVER-RENDERED panel's manual links as well as the promoted
     one's. Those were plain anchors and so downloaded rather than previewed —
     this is a fix carried in with the port, not just a move.

     Shopify's CDN often serves metaobject file references with
     `Content-Disposition: attachment`, which forces a download. Re-fetching
     the file and handing the browser a Blob with an explicit MIME type
     bypasses that. The anchor keeps a real href throughout, so anything that
     goes wrong — CORS, network, a blocked popup, a modifier-click — falls
     back to ordinary navigation. */
  function mimeFor(contentType, url) {
    const type = normalise(contentType).split(';')[0].toLowerCase();
    const isUseless = !type || type === 'application/octet-stream' || type === 'binary/octet-stream';
    if (!isUseless) return type;

    const path = url.split('?')[0].toLowerCase();
    if (path.endsWith('.pdf')) return 'application/pdf';
    if (path.endsWith('.png')) return 'image/png';
    if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
    return 'application/pdf';
  }

  function openInTab(event, url) {
    /* Let the browser handle middle-click, cmd/ctrl-click and shift-click. */
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    /* Open the tab synchronously so it stays inside the user gesture — doing
       it after the await would be caught by popup blockers. */
    const tab = window.open('', '_blank');
    if (!tab) return; // blocked: the anchor's own href takes over

    event.preventDefault();

    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob().then(blob => ({ blob, contentType: response.headers.get('content-type') }));
      })
      .then(({ blob, contentType }) => {
        const typed = new Blob([blob], { type: mimeFor(contentType, url) });
        const objectUrl = URL.createObjectURL(typed);
        tab.location.replace(objectUrl);
        /* Revoking immediately would blank the new tab. */
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch(error => {
        console.warn('[IntexModelPanel] could not re-wrap manual, opening directly.', { url, error });
        tab.location.replace(url);
      });
  }

  /* Delegated once per document, so it covers a panel the script filled, a
     panel Liquid rendered, and the rows inside a <details> that opens later. */
  function bindManuals() {
    if (window.__intexPanelManualsBound) return;
    window.__intexPanelManualsBound = true;

    document.addEventListener('click', event => {
      const link = event.target.closest('[data-intex-model-panel] .intex-btn[href], [data-intex-model-panel] .intex-manuals__link[href]');
      if (!link) return;

      /* Change model is not a manual — it has its own handler above, and it
         is the one .intex-btn in the panel that is not a file. */
      if (link.hasAttribute('data-intex-open-finder')) return;

      const url = link.getAttribute('href');
      if (!url || url === '#') return;

      openInTab(event, url);
    });
  }

  function start() {
    bindManuals();
    bindChangeModel();

    const root = shell();
    if (!root) return;

    const params = new URLSearchParams(window.location.search);
    const gid = params.get(MODEL_PARAM);

    /* The inline script in the section has already removed the shell when
       there is no GID, so this is belt and braces rather than the real guard. */
    if (!gid) {
      root.closest('[data-intex-mc-promote]')?.remove();
      return;
    }

    fillFromUrl(root, gid, normalise(params.get('model_title')));

    const cached = readCache();
    const fromCache = cached && cached.find(model => model.id === gid);
    if (fromCache) {
      fill(root, fromCache);
      return;
    }

    loadModels()
      .then(models => {
        const model = models.find(candidate => candidate.id === gid);
        if (!model) {
          /* The name from the URL is all there is. Drop the skeleton so the
             panel stops pretending more is coming. */
          root.classList.remove('intex-panel--skeleton');
          console.warn('[IntexModelPanel] no model matched the GID in the URL.', { gid });
          return;
        }
        fill(root, model);
      })
      .catch(error => {
        root.classList.remove('intex-panel--skeleton');
        console.warn('[IntexModelPanel] could not load model data.', error);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* The theme editor re-renders the section in place; the new panels need
     Change model bound again. The shell fill is not repeated — the editor
     never has a GID in its URL. */
  document.addEventListener('shopify:section:load', bindChangeModel);
})();